import React, { useEffect, useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import toast from "react-hot-toast";
import {
  retrieveAllBlendsApi,
  retrieveBlendVersionsApi,
  retrieveVersionItemsApi,
  createBlendVersionApi,
  createVersionItemApi,
} from "../../../../components/api/BlendApiService";
import { retrieveFastFilteredShippingInstructionsApi, retrieveShippingInstructionByIdApi } from "../../../../components/api/ShippingInstructionApiService";
import {
  createBatchForBlendVersionApi,
  updateBatchApi,
} from "../../../../components/api/BatchApiService";
import { retrieveAdminCostRateByYearApi } from "../../../../components/api/AdminCostRateApiService";
import VirtualizedExcelInput from "../../../../utils/excelInput";

// ─── Parsing helpers ────────────────────────────────────────────────────────

/**
 * Parse European-format number string to float.
 * "2.496,00" → 2496.00 | "1,12" → 1.12 | "237,79 EUR" → 237.79
 */
const parseEuroNum = (val) => {
  if (val === null || val === undefined) return 0;
  const s = String(val).trim().replace(/[^\d,.-]/g, "");
  if (!s) return 0;
  const hasDot = s.includes(".");
  const hasComma = s.includes(",");
  if (hasDot && hasComma) {
    return parseFloat(s.replace(/\./g, "").replace(",", ".")) || 0;
  }
  if (hasComma && !hasDot) {
    return parseFloat(s.replace(",", ".")) || 0;
  }
  return parseFloat(s) || 0;
};

/** Convert totalPrice to EUR using the given FX rates */
const calcToEur = (totalOrig, currency, eurHuf, usdHuf) => {
  const val = typeof totalOrig === "number" ? totalOrig : parseEuroNum(totalOrig);
  if (!val || isNaN(val)) return 0;
  const curr = (currency || "").trim().toUpperCase();
  const eur = parseFloat(eurHuf);
  const usd = parseFloat(usdHuf);
  if (curr === "EUR") return val;
  if (curr === "USD" && eur > 0 && usd > 0) return val * (usd / eur);
  if (curr === "HUF" && eur > 0) return val / eur;
  return val;
};

const hasActiveRecordStatus = (si) => String(si?.recordStatus ?? "ACTIVE").toUpperCase() !== "PASSIVE";

const sortShippingInstructionsById = (items = []) =>
  [...items].sort((a, b) => {
    const aId = Number(a?.id ?? Number.MAX_SAFE_INTEGER);
    const bId = Number(b?.id ?? Number.MAX_SAFE_INTEGER);
    if (Number.isNaN(aId) && Number.isNaN(bId)) {
      return String(a?.id ?? "").localeCompare(String(b?.id ?? ""));
    }
    if (Number.isNaN(aId)) return 1;
    if (Number.isNaN(bId)) return -1;
    return aId - bId;
  });

/**
 * Parse blend item cost rows from pasted table data.
 *
 * Exact column layout (0-indexed, matches the Excel left table):
 *  0  = Item Code
 *  1  = Item Name
 *  2  = Quantity
 *  3  = Unit
 *  4  = Unit price value
 *  5  = Unit price currency
 *  6  = Total price value
 *  7  = Total price currency
 *  8  = Price $ / HUF -> EUR  (total price converted to EUR)
 *  9  = Customs Duty description
 *  10 = Rate (customs duty rate / HS code)
 *  11 = Customs Duty EUR value
 *  12 = (EUR currency label – skip)
 *  13 = Total cost
 */
const parseItemCostRows = (tableData, versionItems, eurHuf, usdHuf) => {
  if (!tableData || tableData.length === 0) return [];
  return tableData
    .filter((row) => {
      if (!row || row.length < 3) return false;
      const code = String(row[0] || "").trim();
      if (!code) return false;
      return parseEuroNum(row[2]) > 0;
    })
    .map((row) => {
      const get = (i) => String(row[i] || "").trim();
      const itemCode           = get(0);
      const itemName           = get(1);
      const quantity           = parseEuroNum(row[2]);
      const unit               = get(3);
      const unitPriceValue     = parseEuroNum(row[4]);
      const unitPriceCurrency  = get(5).toUpperCase();
      const totalPriceValue    = parseEuroNum(row[6]);
      const totalPriceCurrency = get(7).toUpperCase();
      const totalPriceEur      = parseEuroNum(row[8]);  // Price $ / HUF -> EUR
      const customsDuty        = get(9);
      const customsDutyRate    = get(10);
      const customsDutyEur     = parseEuroNum(row[11]);
      // col 12 is the "EUR" currency label; col 13 is Total cost
      const totalCostEur       = parseEuroNum(row[13]) || parseEuroNum(row[12]);

      const _code = itemCode.toUpperCase();
      const matchedBlendItem = (versionItems || []).find((bi) => {
        const cgm = (bi.shippingInstruction?.shipmentRequest?.customerGradeMark || "").trim().toUpperCase();
        if (!cgm) return false;
        return cgm === _code || cgm.startsWith(_code) || _code.startsWith(cgm);
      });

      return {
        itemCode, itemName, quantity, unit,
        unitPriceValue, unitPriceCurrency,
        totalPriceValue, totalPriceCurrency,
        totalPriceEur,
        customsDuty, customsDutyRate, customsDutyEur,
        totalCostEur,
        matched: !!matchedBlendItem,
        matchedBlendItem,
      };
    });
};

/**
 * Parse non-item (packaging / other materials) rows.
 *
 * Exact column layout (0-indexed, matches the Excel right table):
 *  0  = Code
 *  1  = Name
 *  2  = Quantity
 *  3  = Unit
 *  4  = Unit price value
 *  5  = Unit price currency
 *  6  = Total price value
 *  7  = Total price currency
 *  8  = Price $ / HUF -> EUR  (total price converted to EUR)
 *  9  = Total cost
 */
const parsePackagingRows = (tableData, eurHuf, usdHuf) => {
  if (!tableData || tableData.length === 0) return [];
  return tableData
    .filter((row) => {
      if (!row || row.length < 3) return false;
      const code = String(row[0] || "").trim();
      if (!code) return false;
      return parseEuroNum(row[2]) > 0;
    })
    .map((row) => {
      const get = (i) => String(row[i] || "").trim();
      const itemCode           = get(0);
      const name               = get(1);
      const quantity           = parseEuroNum(row[2]);
      const unit               = get(3);
      const unitPriceValue     = parseEuroNum(row[4]);
      const unitPriceCurrency  = get(5).toUpperCase();
      const totalPriceValue    = parseEuroNum(row[6]);
      const totalPriceCurrency = get(7).toUpperCase();
      const totalPriceEur      = parseEuroNum(row[8]);  // Price $ / HUF -> EUR
      const totalCostEur       = parseEuroNum(row[9]);

      return {
        itemCode, name, quantity, unit,
        unitPriceValue, unitPriceCurrency,
        totalPriceValue, totalPriceCurrency,
        totalPriceEur, totalCostEur,
      };
    });
};

/**
 * Normalise various date formats to YYYY-MM-DD for <input type="date">.
 * Handles: YYYY.MM.DD  YYYY-MM-DD  YYYY/MM/DD  DD.MM.YYYY  DD/MM/YYYY
 */
const parseDateStr = (val) => {
  if (!val) return "";
  const s = String(val).trim();
  // YYYY.MM.DD  YYYY-MM-DD  YYYY/MM/DD
  const m1 = s.match(/^(\d{4})[.\-/](\d{1,2})[.\-/](\d{1,2})$/);
  if (m1) return `${m1[1]}-${m1[2].padStart(2, "0")}-${m1[3].padStart(2, "0")}`;
  // DD.MM.YYYY  DD/MM/YYYY
  const m2 = s.match(/^(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{4})$/);
  if (m2) return `${m2[3]}-${m2[2].padStart(2, "0")}-${m2[1].padStart(2, "0")}`;
  return s;
};

/**
 * Parse the exchange-rates block pasted from the bottom-right of the Excel.
 *
 * Fixed layout (rows 0-indexed):
 *  Row 0: ["Processing Date", "15.08.2025", ""]
 *  Row 1: ["MNB Rate",        "14.08.2025", ""]
 *  Row 2: ["",                "395,44",     "EUR/HUF"]
 *  Row 3: ["",                "338,91",     "USD/HUF"]
 */
const parseRatesFromTable = (tableData) => {
  const result = { processingDate: "", mnbRateDate: "", eurHuf: "", usdHuf: "" };
  if (!tableData || tableData.length < 4) return result;
  const get = (r, c) => String((tableData[r] || [])[c] || "").trim();
  result.processingDate = parseDateStr(get(0, 1));
  result.mnbRateDate    = parseDateStr(get(1, 1));
  // rows 2 & 3: value in col 1, currency label in col 2
  for (let r = 2; r < tableData.length; r++) {
    const val   = get(r, 1);
    const label = get(r, 2).toUpperCase();
    if (!val) continue;
    const n = parseEuroNum(val);
    if (n <= 0) continue;
    if (label.includes("EUR")) result.eurHuf = String(n);
    else if (label.includes("USD")) result.usdHuf = String(n);
  }
  return result;
};

/**
 * Parse the ENTIRE copied Excel range in one pass.
 * Supports two Excel template layouts automatically:
 *
 * OLD layout — separate value / currency columns:
 *   Left  (14 cols): +0=Code +1=Name +2=Qty +3=Unit +4=UnitPriceVal +5=UnitPriceCur
 *                    +6=TotalPriceVal +7=TotalPriceCur +8=PriceToEUR
 *                    +9=CustomsDuty +10=Rate +11=CustomsDutyEUR +12=EURlabel +13=TotalCost
 *   Right (10 cols): +0=Code +1=Name +2=Qty +3=Unit +4=UnitPriceVal +5=UnitPriceCur
 *                    +6=TotalPriceVal +7=TotalPriceCur +8=PriceToEUR +9=TotalCost
 *
 * NEW layout — value+currency combined in one cell (e.g. "3,39 EUR"):
 *   Left  (11 cols): +0=Code +1=Name +2=Qty +3=Unit +4=UnitPrice(combined)
 *                    +5=TotalPrice(combined) +6=PriceToEUR
 *                    +7=CustomsDuty +8=Rate +9=CustomsDutyEUR +10=TotalCost
 *   Right  (8 cols): +0=Code +1=Name +2=Qty +3=Unit +4=UnitPrice(combined)
 *                    +5=TotalPrice(combined) +6=PriceToEUR +7=TotalCost
 *
 * Rates block (U–W, same R offset):
 *   Old: +0=Label  +1=NumericValue  +2=CurrencyLabel ("EUR/HUF" or "USD/HUF")
 *   New: +0=Label  +1=CombinedValue ("379,21 EUR/HUF")  +2=empty
 *
 * The copy can start from any column (A, B, or C); L is auto-detected by majority vote.
 * R is independently voted from offsets 16–22.
 */
const parseWholeSheet = (tableData, versionItems) => {
  const result = {
    processingDate: "",
    mnbRateDate:    "",
    eurHuf:         "",
    usdHuf:         "",
    itemCosts:      [],
    packagingCosts: [],
    // Primary & DIET process header fields
    primaryId:              "",
    primaryCode:            "",
    primaryGradeName:       "",
    primaryProcessingDate:  "",
    rawIn:                  "",
    primaryOut:             "",
    dietIn:                 "",
    dietOut:                "",
    dietId:                 "",
    dietCode:               "",
    dietGradeName:          "",
    primaryBatchNumber:     "",
    processingFeeRate:       "",
  };
  if (!tableData || tableData.length === 0) return result;

  const get       = (row, i) => String(row[i] ?? "").trim();
  const isCode    = (s) => !!s && !s.includes(" ") && s.length >= 3 && !/^\d/.test(s);
  const getCur    = (s) => (String(s).match(/\b(EUR|USD|HUF)\b/i) || [""])[0].toUpperCase();

  // ── First pass: vote on L and R offsets ───────────────────────────────────
  // Strict price check for voting keeps header/metadata rows from polluting the vote.
  // Zero-price rows (unit price = 0) still excluded from voting but will be
  // included in extraction (see second pass).
  const lv = {}, rv = {};
  for (const row of tableData) {
    if (!row || row.length < 5) continue;
    for (let off = 0; off <= 4; off++) {
      if (!isCode(get(row, off))) continue;
      if (parseEuroNum(row[off + 2]) <= 0) continue;
      // Accept price at any candidate total-cost position (old +13, new +10)
      // or unit price position (+4 is common to both layouts)
      if (
        parseEuroNum(row[off + 4])  > 0 ||
        parseEuroNum(row[off + 10]) > 0 ||
        parseEuroNum(row[off + 12]) > 0 ||
        parseEuroNum(row[off + 13]) > 0
      ) { lv[off] = (lv[off] || 0) + 1; break; }
    }
    for (let off = 16; off <= 22; off++) {
      if (!isCode(get(row, off))) continue;
      if (parseEuroNum(row[off + 2]) <= 0) continue;
      // Old total-cost at +9, new at +7; unit price at +4 common to both
      if (
        parseEuroNum(row[off + 4]) > 0 ||
        parseEuroNum(row[off + 7]) > 0 ||
        parseEuroNum(row[off + 9]) > 0
      ) { rv[off] = (rv[off] || 0) + 1; break; }
    }
  }
  const L = Object.keys(lv).length
    ? Number(Object.entries(lv).sort((a, b) => b[1] - a[1])[0][0])
    : 0;
  const R = Object.keys(rv).length
    ? Number(Object.entries(rv).sort((a, b) => b[1] - a[1])[0][0])
    : L + 18;

  // ── Detect column layout (OLD vs NEW) by majority vote ───────────────────
  // OLD left layout: row[L+5] is a pure 3-char ISO currency ("EUR","USD","HUF")
  // NEW left layout: row[L+5] is a combined total-price like "290,00 USD"
  let oldL = 0, newL = 0, oldR = 0, newR = 0;
  for (const row of tableData) {
    if (!row) continue;
    if (isCode(get(row, L)) && parseEuroNum(row[L + 2]) > 0 && parseEuroNum(row[L + 4]) > 0) {
      /^(EUR|USD|HUF)$/i.test(get(row, L + 5)) ? oldL++ : newL++;
    }
    if (isCode(get(row, R)) && parseEuroNum(row[R + 2]) > 0 && parseEuroNum(row[R + 4]) > 0) {
      /^(EUR|USD|HUF)$/i.test(get(row, R + 5)) ? oldR++ : newR++;
    }
  }
  const leftOld  = oldL  >= newL;
  const rightOld = oldR  >= newR;

  console.debug(
    `[parseWholeSheet] L=${L} R=${R}`,
    `| left=${leftOld ? "14-col" : "11-col"}(${oldL}v${newL})`,
    `| right=${rightOld ? "10-col" : "8-col"}(${oldR}v${newR})`
  );

  // ── Header field scan (Production IDs, Blend codes, quantities) ───────────
  // Looks for label-value pairs in the header area of the Excel template.
  // Columns < R belong to the Primary process; columns >= R belong to DIET.
  {
    const norm = (s) => String(s ?? "").trim().toLowerCase().replace(/\s+/g, " ");
    for (const row of tableData) {
      if (!row) continue;
      for (let ci = 0; ci < row.length - 1; ci++) {
        const label = norm(row[ci]);
        const val   = get(row, ci + 1);
        const valNear = val || get(row, ci + 2) || get(row, ci + 3) || get(row, ci + 4);

        // Header values in this template often have spacer columns between label and value.
        // Scan a short window to the right and take the first positive numeric value.
        const numNear = (() => {
          for (let k = 1; k <= 6; k++) {
            const raw = get(row, ci + k);
            if (!raw) continue;
            const n = parseEuroNum(raw);
            if (n > 0) return n;
          }
          return 0;
        })();

        // "Processing Fee" may appear as "Processing Fee:", "Processing Fee (EUR/kg)" etc.
        // The value cell may also have an empty spacer column in between — so look at ci+2 too.
        // Also handle the rare case where value is embedded in the label cell itself.
        if (label.includes("processing fee") && !result.processingFeeRate) {
          const numStr = val || get(row, ci + 2);
          let n = parseEuroNum(numStr);
          if (!(n > 0)) n = parseEuroNum(get(row, ci)); // embedded: "Processing Fee: 1,6"
          if (n > 0) {
            result.processingFeeRate = String(n);
            console.debug("[parseWholeSheet] processingFeeRate found:", n, "at ci=", ci, "val=", val, "ci+2=", get(row, ci + 2));
          }
        }

        const primary = ci < R;
        switch (label) {
          case "production id":
            if (primary  && valNear && !result.primaryId)   result.primaryId              = valNear;
            if (!primary && valNear && !result.dietId)      result.dietId                 = valNear;
            break;
          case "blend id":
            if (primary  && valNear && !result.primaryCode) result.primaryCode            = valNear;
            if (!primary && valNear && !result.dietCode)    result.dietCode               = valNear;
            break;
          case "blend name":
            if (primary  && valNear && !result.primaryGradeName) result.primaryGradeName  = valNear;
            if (!primary && valNear && !result.dietGradeName)    result.dietGradeName     = valNear;
            break;
          case "input": {
            const n = numNear;
            if (n > 0) {
              if (primary  && !result.rawIn)                result.rawIn                  = String(n);
              if (!primary && !result.dietIn)               result.dietIn                 = String(n);
            }
            break;
          }
          case "output": {
            const n = numNear;
            if (n > 0) {
              if (primary  && !result.primaryOut)           result.primaryOut             = String(n);
              if (!primary && !result.dietOut)              result.dietOut                = String(n);
            }
            break;
          }
          case "batch number":
            if (primary  && valNear && !result.primaryBatchNumber) result.primaryBatchNumber = valNear;
            break;
          case "processing fee": {
            // Already handled above (before the empty-val guard)
            break;
          }
          case "processing date": {
            const d = parseDateStr(valNear);
            if (d && primary && !result.primaryProcessingDate) result.primaryProcessingDate = d;
            break;
          }
          default:
            break;
        }
      }
    }
  }

  // ── Second pass: extract rows ──────────────────────────────────────────────
  for (const row of tableData) {
    if (!row || row.length < 2) continue;

    const leftCode  = get(row, L);
    const rightCode = get(row, R);
    const rightLabel = rightCode.toLowerCase();

    // ── Rates block ─────────────────────────────────────────────────────────
    // NOTE: no `continue` — the rates block on the right side shares rows with
    // the last left-table items, so we must fall through to extract them too.
    if (rightLabel.includes("processing")) {
      const d = parseDateStr(get(row, R + 1));
      if (d) result.processingDate = d;
    } else if (rightLabel.includes("mnb")) {
      const d = parseDateStr(get(row, R + 1));
      if (d) result.mnbRateDate = d;
    } else if (!rightCode) {
      // Rate rows: old layout has currency label in R+2; new layout embeds it in R+1
      const rateRaw = get(row, R + 1);
      const rateCur = (get(row, R + 2) || rateRaw).toUpperCase(); // works for both layouts
      if (rateCur.includes("EUR") && rateCur.includes("/")) {
        const n = parseEuroNum(rateRaw);
        if (n > 0) result.eurHuf = String(n);
      }
      if (rateCur.includes("USD") && rateCur.includes("/")) {
        const n = parseEuroNum(rateRaw);
        if (n > 0) result.usdHuf = String(n);
      }
    }

    // ── Left table item row ─────────────────────────────────────────────────
    // No price > 0 requirement here — zero-unit-price items are valid
    if (isCode(leftCode) && parseEuroNum(row[L + 2]) > 0) {
      const _lcode = leftCode.toUpperCase();
      const matchedBlendItem = (versionItems || []).find((bi) => {
        const cgm = (bi.shippingInstruction?.shipmentRequest?.customerGradeMark || "").trim().toUpperCase();
        if (!cgm) return false;
        return cgm === _lcode || cgm.startsWith(_lcode) || _lcode.startsWith(cgm);
      });
      let fields;
      if (leftOld) {
        // 14-column layout: separate value / currency cells
        fields = {
          unitPriceValue:     parseEuroNum(row[L + 4]),
          unitPriceCurrency:  get(row, L + 5).toUpperCase(),
          totalPriceValue:    parseEuroNum(row[L + 6]),
          totalPriceCurrency: get(row, L + 7).toUpperCase(),
          totalPriceEur:      parseEuroNum(row[L + 8]),
          customsDuty:        get(row, L + 9),
          customsDutyRate:    get(row, L + 10),
          customsDutyEur:     parseEuroNum(row[L + 11]),
          totalCostEur:       parseEuroNum(row[L + 13]) || parseEuroNum(row[L + 12]),
        };
      } else {
        // 11-column layout: combined value+currency cells
        fields = {
          unitPriceValue:     parseEuroNum(row[L + 4]),
          unitPriceCurrency:  getCur(row[L + 4]),
          totalPriceValue:    parseEuroNum(row[L + 5]),
          totalPriceCurrency: getCur(row[L + 5]),
          totalPriceEur:      parseEuroNum(row[L + 6]),
          customsDuty:        get(row, L + 7),
          customsDutyRate:    get(row, L + 8),
          customsDutyEur:     parseEuroNum(row[L + 9]),
          totalCostEur:       parseEuroNum(row[L + 10]),
        };
      }
      result.itemCosts.push({
        itemCode:  leftCode,
        itemName:  get(row, L + 1),
        quantity:  parseEuroNum(row[L + 2]),
        unit:      get(row, L + 3),
        ...fields,
        matched: !!matchedBlendItem,
        matchedBlendItem,
      });
      // *** do NOT continue — fall through so the right side of this same row is also checked
    }

    // ── Right table packaging row ────────────────────────────────────────────
    // Checked independently even when a left-table row was found above,
    // because both tables' data rows occupy the exact same spreadsheet rows.
    if (isCode(rightCode) && !rightLabel.includes("code") && parseEuroNum(row[R + 2]) > 0) {
      let pkg;
      if (rightOld) {
        // 10-column layout: separate value / currency cells
        pkg = {
          unitPriceValue:     parseEuroNum(row[R + 4]),
          unitPriceCurrency:  get(row, R + 5).toUpperCase(),
          totalPriceValue:    parseEuroNum(row[R + 6]),
          totalPriceCurrency: get(row, R + 7).toUpperCase(),
          totalPriceEur:      parseEuroNum(row[R + 8]),
          totalCostEur:       parseEuroNum(row[R + 9]),
        };
      } else {
        // 8-column layout: combined value+currency cells
        pkg = {
          unitPriceValue:     parseEuroNum(row[R + 4]),
          unitPriceCurrency:  getCur(row[R + 4]),
          totalPriceValue:    parseEuroNum(row[R + 5]),
          totalPriceCurrency: getCur(row[R + 5]),
          totalPriceEur:      parseEuroNum(row[R + 6]),
          totalCostEur:       parseEuroNum(row[R + 7]),
        };
      }
      result.packagingCosts.push({
        itemCode: rightCode,
        name:     get(row, R + 1),
        quantity: parseEuroNum(row[R + 2]),
        unit:     get(row, R + 3),
        ...pkg,
      });
      continue;
    }
  }

  return result;
};

/** Convert ISO date string to millisecond timestamp for the API */
const dateToMs = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T00:00:00.000Z");
  return isNaN(d.getTime()) ? null : d.getTime();
};

const fmtEur = (val) =>
  val != null && !isNaN(val) ? `${Number(val).toFixed(4)} EUR` : "—";

// ─── Column legend component ────────────────────────────────────────────────

function ColumnLegend({ columns }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 0,
        mb: 1,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        overflow: "hidden",
        width: "fit-content",
      }}
    >
      {columns.map((col, idx) => (
        <Box
          key={col.col}
          sx={{
            px: 1.5,
            py: 0.5,
            bgcolor: col.color,
            borderRight: idx < columns.length - 1 ? "1px solid" : "none",
            borderColor: "divider",
            textAlign: "center",
            minWidth: 72,
          }}
        >
          <Typography variant="caption" fontWeight="bold" display="block">
            {col.col}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ lineHeight: 1.2 }}
          >
            {col.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

// ─── Preview tables ─────────────────────────────────────────────────────────

function ParsedItemsTable({ rows }) {
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, maxHeight: 280 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: "bold" }}>Match</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Item Code</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Item Name</TableCell>
            <TableCell sx={{ fontWeight: "bold" }} align="right">Qty</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Unit</TableCell>
            <TableCell sx={{ fontWeight: "bold" }} align="right">Total Price</TableCell>
            <TableCell sx={{ fontWeight: "bold" }} align="right">Price (EUR calc.)</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Customs Duty</TableCell>
            <TableCell sx={{ fontWeight: "bold" }} align="right">Customs EUR</TableCell>
            <TableCell sx={{ fontWeight: "bold" }} align="right">Total Cost EUR</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow
              key={i}
              sx={{ bgcolor: r.matched ? "rgba(76,175,80,0.07)" : "rgba(244,67,54,0.07)" }}
            >
              <TableCell>
                {r.matched ? (
                  <Tooltip
                    title={`Matched: ${
                      r.matchedBlendItem?.shippingInstruction?.shipmentRequest
                        ?.customerGradeMark || ""
                    }`}
                  >
                    <CheckCircleIcon color="success" fontSize="small" />
                  </Tooltip>
                ) : (
                  <Tooltip title="No matching blend item found for this code">
                    <WarningAmberIcon color="warning" fontSize="small" />
                  </Tooltip>
                )}
              </TableCell>
              <TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
                {r.itemCode}
              </TableCell>
              <TableCell sx={{ fontSize: "0.75rem" }}>{r.itemName}</TableCell>
              <TableCell align="right">{r.quantity}</TableCell>
              <TableCell>{r.unit}</TableCell>
              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                {(r.totalPriceValue || 0).toLocaleString("en", { maximumFractionDigits: 2 })}{" "}
                {r.totalPriceCurrency}
              </TableCell>
              <TableCell align="right">{(r.totalPriceEur || 0).toFixed(2)}</TableCell>
              <TableCell sx={{ fontSize: "0.75rem" }}>{r.customsDuty || "—"}</TableCell>
              <TableCell align="right">{(r.customsDutyEur || 0).toFixed(2)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold" }}>
                {(r.totalCostEur || 0).toFixed(2)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function ParsedPackagingTable({ rows }) {
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, maxHeight: 220 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: "bold" }}>Code</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Name</TableCell>
            <TableCell sx={{ fontWeight: "bold" }} align="right">Qty</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Unit</TableCell>
            <TableCell sx={{ fontWeight: "bold" }} align="right">Total Price</TableCell>
            <TableCell sx={{ fontWeight: "bold" }} align="right">Price (EUR calc.)</TableCell>
            <TableCell sx={{ fontWeight: "bold" }} align="right">Total Cost EUR</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i} sx={{ bgcolor: "rgba(0,0,0,0.02)" }}>
              <TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>{r.itemCode}</TableCell>
              <TableCell sx={{ fontSize: "0.75rem" }}>{r.name}</TableCell>
              <TableCell align="right">{r.quantity}</TableCell>
              <TableCell>{r.unit}</TableCell>
              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                {(r.totalPriceValue || 0).toLocaleString("en", { maximumFractionDigits: 2 })}{" "}
                {r.totalPriceCurrency}
              </TableCell>
              <TableCell align="right">{(r.totalPriceEur || 0).toFixed(2)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold" }}>
                {(r.totalCostEur || 0).toFixed(2)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function CostSummaryRow({ label, value, highlight }) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        px: 1.5,
        py: 0.5,
        bgcolor: highlight ? "primary.50" : "transparent",
        borderRadius: 1,
      }}
    >
      <Typography variant="body2">{label}</Typography>
      <Typography
        variant="body2"
        fontWeight={highlight ? "bold" : "normal"}
        color={highlight ? "primary.main" : "text.primary"}
      >
        {fmtEur(value)}
      </Typography>
    </Stack>
  );
}

// ─── Memoized blend item validation row ──────────────────────────────────────
// Defined outside AddEditBatchDialog so React.memo can work across re-renders.
const BlendItemValidationRow = React.memo(function BlendItemValidationRow({ row, idx, onSIChange }) {
  return (
    <TableRow sx={{
      bgcolor: row.validating
        ? "inherit"
        : row.validated
        ? "rgba(76,175,80,0.07)"
        : row.error?.startsWith("Warning")
        ? "rgba(255,193,7,0.07)"
        : "rgba(244,67,54,0.07)",
    }}>
      <TableCell align="center">
        {row.validating ? (
          <CircularProgress size={16} />
        ) : row.validated ? (
          <Tooltip title="Validated">
            <CheckCircleIcon color="success" fontSize="small" />
          </Tooltip>
        ) : (
          <Tooltip title={row.error || "Not validated"}>
            <WarningAmberIcon color={row.error?.startsWith("Warning") ? "warning" : "error"} fontSize="small" />
          </Tooltip>
        )}
      </TableCell>
      <TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>{row.itemCode}</TableCell>
      <TableCell sx={{ fontSize: "0.75rem" }}>{row.itemName}</TableCell>
      <TableCell align="right">{row.quantity}</TableCell>
      <TableCell>{row.unit}</TableCell>
      <TableCell sx={{ minWidth: 280 }}>
        {row.validating ? (
          <CircularProgress size={14} />
        ) : row.allMatchingSIs && row.allMatchingSIs.length > 1 ? (
          <Select
            size="small"
            value={row.shippingInstruction?.id || ""}
            onChange={(e) => {
              const sel = row.allMatchingSIs.find((si) => si.id === e.target.value);
              if (sel) onSIChange(idx, sel);
            }}
            sx={{ fontSize: "0.75rem", minWidth: 260 }}
          >
            {row.allMatchingSIs.map((si) => (
              <MenuItem key={si.id} value={si.id}
                sx={si._sufficient === false ? { color: "warning.main" } : {}}>
                {si._sufficient === false ? "⚠ " : ""}
                {`SI #${si.finalShippingInstructionNumber || si.id} | Storage: ${si.shipmentRequestStorageQuantity ?? "N/A"} | ${si.shipmentRequestCustomerGradeMark || ""}`}
              </MenuItem>
            ))}
          </Select>
        ) : row.shippingInstruction ? (
          <Typography variant="caption">
            {`${row.shippingInstruction.id} | SI #${row.shippingInstruction.finalShippingInstructionNumber || row.shippingInstruction.id} | Storage: ${row.shippingInstruction.shipmentRequestStorageQuantity ?? "N/A"}`}
          </Typography>
        ) : "—"}
      </TableCell>
      <TableCell sx={{ fontSize: "0.75rem" }}>
        {row.item ? `${row.item.buyingGrade || ""} ${row.item.origin || ""}`.trim() || `ID ${row.item.id}` : "—"}
      </TableCell>
      <TableCell sx={{ fontSize: "0.72rem" }}>
        {row.error ? (
          <Typography variant="caption" color={row.error.startsWith("Warning") ? "warning.main" : "error"}>
            {row.error}
          </Typography>
        ) : "—"}
      </TableCell>
    </TableRow>
  );
});

// ─── Main dialog ─────────────────────────────────────────────────────────────

/**
 * Dialog for adding a new batch or editing an existing one.
 *
 * Props:
 *  open              – boolean
 *  onClose           – () => void
 *  onSaved           – (savedBatch, blendId, versionId) => void
 *  editBatch         – batch object to edit (null = create mode)
 *  initialBlendId    – pre-selected blend id (optional)
 *  initialVersionId  – pre-selected version id (optional)
 */
export default function AddEditBatchDialog({
  open,
  onClose,
  onSaved,
  editBatch,
  initialBlendId,
  initialVersionId,
}) {
  const isEdit = !!editBatch;

  // ── Core batch fields ────────────────────────────────────────────────────
  const [blends, setBlends] = useState([]);
  const [versions, setVersions] = useState([]);
  const [versionItems, setVersionItems] = useState([]);
  const [blendId, setBlendId] = useState("");
  const [versionId, setVersionId] = useState("");
  const [batchNumber, setBatchNumber] = useState("");
  const [dietOutNet, setDietOutNet] = useState("");
  const [recordStatus, setRecordStatus] = useState("ACTIVE");
  const [saving, setSaving] = useState(false);
  const [loadingBlends, setLoadingBlends] = useState(false);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // ── Primary / DIET process info fields ───────────────────────────────────
  const [primaryId, setPrimaryId] = useState("");
  const [primaryCode, setPrimaryCode] = useState("");
  const [primaryGradeName, setPrimaryGradeName] = useState("");
  const [primaryProcessingDate, setPrimaryProcessingDate] = useState("");
  const [rawIn, setRawIn] = useState("");
  const [primaryOut, setPrimaryOut] = useState("");
  const [dietIn, setDietIn] = useState("");
  const [dietId, setDietId] = useState("");
  const [dietCode, setDietCode] = useState("");
  const [dietGradeName, setDietGradeName] = useState("");
  const [processingFeeRate, setProcessingFeeRate] = useState("");
  const [adminCostRate, setAdminCostRate] = useState("0.95");
  // tracks whether admin rate was explicitly set (edit mode or user typed)
  const [adminCostRateOverridden, setAdminCostRateOverridden] = useState(false);

  // ── Cost data fields ─────────────────────────────────────────────────────
  const [eurHufRate, setEurHufRate] = useState("");
  const [usdHufRate, setUsdHufRate] = useState("");
  const [processingDate, setProcessingDate] = useState("");
  const [mnbRateDate, setMnbRateDate] = useState("");
  const [wholeSheetData, setWholeSheetData] = useState([]);
  const [parsedItemCosts, setParsedItemCosts] = useState([]);
  const [parsedPackagingCosts, setParsedPackagingCosts] = useState([]);

  // ── Manual cost entry form state ────────────────────────────────────
  const [manualItemForm, setManualItemForm] = useState({});
  const [manualPackForm, setManualPackForm] = useState({});
  const [showManualItemForm, setShowManualItemForm] = useState(false);
  const [showManualPackForm, setShowManualPackForm] = useState(false);
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [editingPackIndex, setEditingPackIndex] = useState(null);

  // ── "Add version to existing blend" state ──────────────────────────────────
  const [saveAsBlend, setSaveAsBlend] = useState(false);
  const [saveAsBlendId, setSaveAsBlendId] = useState("");  // existing blend to attach the new version to
  const [newVersionName, setNewVersionName] = useState("");
  const [newVersionStatus, setNewVersionStatus] = useState("Actual");
  const [newVersionCutWidth, setNewVersionCutWidth] = useState("");
  const [newVersionMoisture, setNewVersionMoisture] = useState("");
  const [newVersionTemperature, setNewVersionTemperature] = useState("");
  const [newVersionNicotineTolerancePct, setNewVersionNicotineTolerancePct] = useState("");
  const [newVersionSugarTolerancePct, setNewVersionSugarTolerancePct] = useState("");
  const [blendItemRows, setBlendItemRows] = useState([]);  // validated rows for blend version creation
  const [validatingBlendItems, setValidatingBlendItems] = useState(false);
  const [creatingVersion, setCreatingVersion] = useState(false);

  // ── Derived cost totals ──────────────────────────────────────────────────
  const tobaccoPgEur = useMemo(
    () => parsedItemCosts.reduce((s, r) => s + (r.totalCostEur || 0), 0),
    [parsedItemCosts]
  );
  const packagingMaterialEur = useMemo(
    () => parsedPackagingCosts.reduce((s, r) => s + (r.totalCostEur || 0), 0),
    [parsedPackagingCosts]
  );
  const processingFeeTotal = useMemo(() => {
    const rate = parseFloat(processingFeeRate);
    const qty  = Number(dietOutNet);
    return rate > 0 && qty > 0 ? rate * qty : 0;
  }, [processingFeeRate, dietOutNet]);
  const totalCostEurCalc = tobaccoPgEur + packagingMaterialEur + processingFeeTotal;
  const unitPriceEurCalc =
    totalCostEurCalc > 0 && Number(dietOutNet) > 0
      ? totalCostEurCalc / Number(dietOutNet)
      : null;

  // ── Reset on open ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setSaveError(null);
    setParsedItemCosts([]);
    setParsedPackagingCosts([]);
    setWholeSheetData([]);
    setEurHufRate("");
    setUsdHufRate("");
    setProcessingDate("");
    setMnbRateDate("");
    setPrimaryId("");
    setPrimaryCode("");
    setPrimaryGradeName("");
    setPrimaryProcessingDate("");
    setRawIn("");
    setPrimaryOut("");
    setDietIn("");
    setDietId("");
    setDietCode("");
    setDietGradeName("");
    setProcessingFeeRate("");
    setAdminCostRate("0.95");
    setAdminCostRateOverridden(false);
    setSaveAsBlend(false);
    setSaveAsBlendId("");
    setNewVersionName("");
    setNewVersionStatus("Actual");
    setNewVersionCutWidth("");
    setNewVersionMoisture("");
    setNewVersionTemperature("");
    setNewVersionNicotineTolerancePct("");
    setNewVersionSugarTolerancePct("");
    setBlendItemRows([]);
    setValidatingBlendItems(false);
    setCreatingVersion(false);

    if (isEdit) {
      setBatchNumber(editBatch.batchNumber || "");
      setDietOutNet(editBatch.dietOutNet != null ? String(editBatch.dietOutNet) : "");
      setRecordStatus(editBatch.recordStatus || "ACTIVE");
      setPrimaryId(editBatch.primaryId || "");
      setPrimaryCode(editBatch.primaryCode || "");
      setPrimaryGradeName(editBatch.primaryGradeName || "");
      setPrimaryProcessingDate(editBatch.primaryProcessingDate
        ? new Date(editBatch.primaryProcessingDate).toISOString().slice(0, 10)
        : "");
      setRawIn(editBatch.rawIn != null ? String(editBatch.rawIn) : "");
      setPrimaryOut(editBatch.primaryOut != null ? String(editBatch.primaryOut) : "");
      setDietIn(editBatch.dietIn != null ? String(editBatch.dietIn) : "");
      setDietId(editBatch.dietId || "");
      setDietCode(editBatch.dietCode || "");
      setDietGradeName(editBatch.dietGradeName || "");
      // Reverse-compute the per-kg rate from the stored total
      setProcessingFeeRate(
        editBatch.processingFeeEur != null && Number(editBatch.dietOutNet) > 0
          ? String((Number(editBatch.processingFeeEur) / Number(editBatch.dietOutNet)).toFixed(6)).replace(/\.?0+$/, "")
          : ""
      );
      setAdminCostRate(
        editBatch.adminCostEurPerKg != null
          ? String(Number(editBatch.adminCostEurPerKg))
          : "0.95"
      );
      setAdminCostRateOverridden(true);
    } else {
      setBatchNumber("");
      setDietOutNet("");
      setRecordStatus("ACTIVE");
      setVersionId(initialVersionId || "");
      setBlendId(initialBlendId || "");
      setVersions([]);
      setVersionItems([]);

      setLoadingBlends(true);
      retrieveAllBlendsApi()
        .then((res) => setBlends(res?.data || []))
        .catch(() => toast.error("Could not load blends."))
        .finally(() => setLoadingBlends(false));

      if (initialBlendId) {
        setLoadingVersions(true);
        retrieveBlendVersionsApi(initialBlendId)
          .then((res) => setVersions(res?.data || []))
          .catch(() => toast.error("Could not load versions."))
          .finally(() => setLoadingVersions(false));
      }
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-fetch admin cost rate for the current year (create mode only) ──
  useEffect(() => {
    if (!open || isEdit || adminCostRateOverridden) return;
    const year = new Date().getFullYear();
    retrieveAdminCostRateByYearApi(year)
      .then((res) => {
        if (res?.data?.rateEurPerKg) {
          setAdminCostRate(String(Number(res.data.rateEurPerKg)));
        }
      })
      .catch(() => {}); // silently ignore — user can type manually
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Extract tobacco rows from raw paste & validate SIs (runs BEFORE Parse All) ──
  const handleValidateBlendItems = async () => {
    if (!wholeSheetData || wholeSheetData.length === 0) {
      toast.error("Paste the Excel data first (in the 'Cost Info from Excel' section below).");
      return;
    }
    // Extract item rows from raw pasted data without version matching
    const extracted = parseWholeSheet(wholeSheetData, []);
    if (extracted.itemCosts.length === 0) {
      toast.error("No tobacco item rows found in the pasted data.");
      return;
    }
    setValidatingBlendItems(true);
    const initialRows = extracted.itemCosts.map((r) => ({
      itemCode:            r.itemCode,
      itemName:            r.itemName,
      quantity:            r.quantity,
      unit:                r.unit || "Kg",
      validating:          true,
      validated:           false,
      error:               !r.itemCode ? "Missing item code" : null,
      allMatchingSIs:      [],
      shippingInstruction: null,
      item:                null,
    }));
    setBlendItemRows([...initialRows]);
    const updatedRows = [...initialRows];
    for (let i = 0; i < updatedRows.length; i++) {
      const row = updatedRows[i];
      if (row.error) {
        updatedRows[i] = { ...row, validating: false };
        setBlendItemRows([...updatedRows]);
        continue;
      }
      try {
        const siRes = await retrieveFastFilteredShippingInstructionsApi({ shipmentRequestCustomerGradeMark: row.itemCode });
        const raw = siRes.data || [];
        const withStock = raw.filter((si) => hasActiveRecordStatus(si) && Number(si.shipmentRequestStorageQuantity) > 0);
        if (withStock.length === 0) {
          updatedRows[i] = {
            ...row,
            validating: false,
            error: raw.length
              ? `No SI with storage qty > 0 for "${row.itemCode}"`
              : `No Shipping Instruction found for "${row.itemCode}"`,
          };
          setBlendItemRows([...updatedRows]);
          continue;
        }
        const sortedWithStock = sortShippingInstructionsById(withStock);
        const allMatchingSIs = sortedWithStock.map((si) => ({
          ...si,
          _sufficient: Number(si.shipmentRequestStorageQuantity) >= row.quantity,
        }));
        const defaultSI = sortedWithStock[0];
        const insufficientWarning = !sortedWithStock.some((si) => Number(si.shipmentRequestStorageQuantity) >= row.quantity)
          ? `Warning: no SI has sufficient stock (need ${row.quantity}). Review selection.`
          : null;
        const shippingInstruction = allMatchingSIs.find((si) => String(si.id) === String(defaultSI.id)) || allMatchingSIs[0];

        const siDetail = await retrieveShippingInstructionByIdApi(defaultSI.id);
        const foundItem =
          siDetail.data.shipmentRequest?.purchaseOrder?.offer?.item ||
          siDetail.data.shipmentRequest?.salesOrder?.offer?.item ||
          null;

        updatedRows[i] = {
          ...row,
          validating:          false,
          allMatchingSIs,
          shippingInstruction,
          item:      foundItem || null,
          validated: !!foundItem,
          error:     foundItem ? insufficientWarning : `No item found in SI #${defaultSI.id}`,
        };
        setBlendItemRows([...updatedRows]);
      } catch (err) {
        updatedRows[i] = {
          ...row,
          validating: false,
          validated:  false,
          error:      `Validation failed: ${err.message || "Unknown error"}`,
        };
        setBlendItemRows([...updatedRows]);
      }
    }
    const validCount = updatedRows.filter((r) => r.validated).length;
    const errCount   = updatedRows.filter((r) => r.error && !r.error.startsWith("Warning")).length;
    if (validCount > 0 && errCount === 0) toast.success(`All ${validCount} blend item(s) validated.`);
    else if (validCount > 0) toast.success(`${validCount} validated, ${errCount} with errors.`);
    else toast.error("No blend items could be validated.");
    setValidatingBlendItems(false);
  };

  // ── SI change per blend item row ─────────────────────────────────────────
  const handleBlendItemRowSIChange = useCallback(async (rowIdx, newSI) => {
    setBlendItemRows((prev) =>
      prev.map((r, i) =>
        i === rowIdx
          ? { ...r, shippingInstruction: newSI, item: null, validated: false, error: null, validating: true }
          : r
      )
    );
    try {
      const siDetail = await retrieveShippingInstructionByIdApi(newSI.id);
      const foundItem =
        siDetail.data.shipmentRequest?.purchaseOrder?.offer?.item ||
        siDetail.data.shipmentRequest?.salesOrder?.offer?.item ||
        null;
      setBlendItemRows((prev) =>
        prev.map((r, i) =>
          i === rowIdx
            ? { ...r, item: foundItem || null, validated: !!foundItem, error: foundItem ? null : `No item in SI #${newSI.id}`, validating: false }
            : r
        )
      );
    } catch (err) {
      setBlendItemRows((prev) =>
        prev.map((r, i) =>
          i === rowIdx
            ? { ...r, item: null, validated: false, error: `Failed to fetch SI: ${err.message || "error"}`, validating: false }
            : r
        )
      );
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Create blend version now (before batch creation) ────────────────────
  const handleCreateVersionNow = async () => {
    if (!saveAsBlendId) { toast.error("Select an existing Blend first."); return; }
    if (!newVersionName.trim()) { toast.error("Enter a Version Name."); return; }
    const validRows = blendItemRows.filter((r) => r.validated && r.item && r.shippingInstruction);
    if (validRows.length === 0) { toast.error("Validate blend items first."); return; }

    setCreatingVersion(true);
    try {
      // 1. Create the version
      const versionRes = await createBlendVersionApi(saveAsBlendId, {
        versionName: newVersionName,
        status:      newVersionStatus || "Actual",
        ...(newVersionCutWidth             ? { cutWidth:             newVersionCutWidth }                           : {}),
        ...(newVersionMoisture             ? { moisture:             newVersionMoisture }                           : {}),
        ...(newVersionTemperature          ? { temperature:          newVersionTemperature }                        : {}),
        ...(newVersionNicotineTolerancePct ? { nicotineTolerancePct: Number(newVersionNicotineTolerancePct) }       : {}),
        ...(newVersionSugarTolerancePct    ? { sugarTolerancePct:    Number(newVersionSugarTolerancePct) }          : {}),
      });
      const newVersionId = versionRes?.data?.id;
      if (!newVersionId) throw new Error("Version creation returned no ID.");

      // 2. Create blend items from validated rows
      let createdCount = 0;
      for (const row of validRows) {
        try {
          await createVersionItemApi(saveAsBlendId, newVersionId, {
            item:                { id: row.item.id },
            shippingInstruction: { id: row.shippingInstruction.id },
            quantity:            row.quantity,
            unit:                row.unit || "Kg",
          });
          createdCount++;
        } catch (err) {
          console.warn(`Blend item ${row.itemCode} skipped:`, err?.response?.data?.message || err.message);
        }
      }

      // 3. Load the new version's items so batch parsing can use them for matching
      const [itemsRes, versionsRes] = await Promise.all([
        retrieveVersionItemsApi(saveAsBlendId, newVersionId),
        retrieveBlendVersionsApi(saveAsBlendId),
      ]);
      const loadedItems = itemsRes?.data || [];
      setVersions(versionsRes?.data || []);
      setBlendId(saveAsBlendId);
      setVersionId(newVersionId);
      setVersionItems(loadedItems);

      // 4. Auto-fill dietOutNet from total item qty if not yet set
      if (!dietOutNet) {
        const total = loadedItems.reduce((s, it) => s + (Number(it.quantity) || 0), 0);
        if (total > 0) setDietOutNet(String(total));
      }

      // 5. Re-run parse if Excel is already present so matching uses the new version
      if (wholeSheetData.length > 0) {
        const parsed = parseWholeSheet(wholeSheetData, loadedItems);
        if (parsed.processingDate) setProcessingDate(parsed.processingDate);
        if (parsed.mnbRateDate)    setMnbRateDate(parsed.mnbRateDate);
        if (parsed.eurHuf)         setEurHufRate(parsed.eurHuf);
        if (parsed.usdHuf)         setUsdHufRate(parsed.usdHuf);
        setParsedItemCosts(parsed.itemCosts);
        setParsedPackagingCosts(parsed.packagingCosts);
        if (parsed.primaryId)             setPrimaryId(parsed.primaryId);
        if (parsed.primaryCode)           setPrimaryCode(parsed.primaryCode);
        if (parsed.primaryGradeName)      setPrimaryGradeName(parsed.primaryGradeName);
        if (parsed.primaryProcessingDate) setPrimaryProcessingDate(parsed.primaryProcessingDate);
        if (parsed.rawIn)                 setRawIn(parsed.rawIn);
        if (parsed.primaryOut)            setPrimaryOut(parsed.primaryOut);
        if (parsed.dietIn)                setDietIn(parsed.dietIn);
        if (parsed.dietOut)               setDietOutNet(parsed.dietOut);
        if (parsed.dietId)                setDietId(parsed.dietId);
        if (parsed.dietCode)              setDietCode(parsed.dietCode);
        if (parsed.dietGradeName)         setDietGradeName(parsed.dietGradeName);
        if (parsed.processingFeeRate)     setProcessingFeeRate(parsed.processingFeeRate);
        if (parsed.primaryBatchNumber && !batchNumber) setBatchNumber(parsed.primaryBatchNumber);
      }

      const blendLabel = blends.find((b) => String(b.id) === String(saveAsBlendId))?.code
        || blends.find((b) => String(b.id) === String(saveAsBlendId))?.name
        || `Blend #${saveAsBlendId}`;
      toast.success(
        `Version "${newVersionName}" created for "${blendLabel}" with ${createdCount} item(s). ` +
        (wholeSheetData.length > 0 ? "Excel re-parsed with new version." : "Now paste and parse the Excel.")
      );
      setSaveAsBlend(false); // collapse the section — version is now selected above
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || "Failed to create blend version.";
      setSaveError(msg);
    } finally {
      setCreatingVersion(false);
    }
  };

  // ── Blend change ─────────────────────────────────────────────────────────
  const handleBlendChange = async (e) => {
    const value = e.target.value;
    setBlendId(value);
    setVersionId("");
    setVersions([]);
    setVersionItems([]);
    setDietOutNet("");
    setParsedItemCosts([]);
    if (!value) return;
    setLoadingVersions(true);
    try {
      const res = await retrieveBlendVersionsApi(value);
      setVersions(res?.data || []);
    } catch {
      toast.error("Could not load versions.");
    } finally {
      setLoadingVersions(false);
    }
  };

  // ── Version change ───────────────────────────────────────────────────────
  const handleVersionChange = async (e) => {
    const value = e.target.value;
    setVersionId(value);
    setDietOutNet("");
    setParsedItemCosts([]);
    setVersionItems([]);
    if (!blendId || !value) return;
    try {
      const res = await retrieveVersionItemsApi(blendId, value);
      const items = res?.data || [];
      setVersionItems(items);
      const total = items.reduce((sum, it) => sum + (Number(it.quantity) || 0), 0);
      if (!Number.isNaN(total) && total > 0) setDietOutNet(String(total));
    } catch {
      // silent – user can enter manually
    }
  };

  // ── Parse whole sheet ────────────────────────────────────────────────────
  const handleParseAll = useCallback(() => {
    const parsed = parseWholeSheet(wholeSheetData, versionItems);
    if (parsed.processingDate) setProcessingDate(parsed.processingDate);
    if (parsed.mnbRateDate)    setMnbRateDate(parsed.mnbRateDate);
    if (parsed.eurHuf)         setEurHufRate(parsed.eurHuf);
    if (parsed.usdHuf)         setUsdHufRate(parsed.usdHuf);
    setParsedItemCosts(parsed.itemCosts);
    setParsedPackagingCosts(parsed.packagingCosts);

    // Auto-populate Primary/DIET process info from header fields
    if (parsed.primaryId)             setPrimaryId(parsed.primaryId);
    if (parsed.primaryCode)           setPrimaryCode(parsed.primaryCode);
    if (parsed.primaryGradeName)      setPrimaryGradeName(parsed.primaryGradeName);
    if (parsed.primaryProcessingDate) setPrimaryProcessingDate(parsed.primaryProcessingDate);
    if (parsed.rawIn)                 setRawIn(parsed.rawIn);
    if (parsed.primaryOut)            setPrimaryOut(parsed.primaryOut);
    if (parsed.dietIn)                setDietIn(parsed.dietIn);
    if (parsed.dietOut)               setDietOutNet(parsed.dietOut);
    if (parsed.dietId)                setDietId(parsed.dietId);
    if (parsed.dietCode)              setDietCode(parsed.dietCode);
    if (parsed.dietGradeName)         setDietGradeName(parsed.dietGradeName);
    if (parsed.processingFeeRate)     setProcessingFeeRate(parsed.processingFeeRate);
    // Auto-fill batch number only if not already filled in
    if (parsed.primaryBatchNumber && !batchNumber) setBatchNumber(parsed.primaryBatchNumber);

    if (parsed.itemCosts.length === 0 && parsed.packagingCosts.length === 0) {
      toast(
        "No data rows found. Make sure item codes are in column C and packaging codes in column U.",
        { icon: "ℹ️" }
      );
      return;
    }
    const parts = [];
    if (parsed.itemCosts.length > 0) {
      const matched = parsed.itemCosts.filter((r) => r.matched).length;
      parts.push(
        `${parsed.itemCosts.length} blend item(s) — ${matched} matched` +
        (matched < parsed.itemCosts.length ? ` (${parsed.itemCosts.length - matched} unmatched, still stored)` : "")
      );
    }
    if (parsed.packagingCosts.length > 0)
      parts.push(`${parsed.packagingCosts.length} packaging row(s)`);
    if (parsed.processingDate || parsed.eurHuf)
      parts.push("exchange rates parsed");
    toast.success(`Parsed: ${parts.join(" · ")}.`);
  }, [wholeSheetData, versionItems]);

  // ── Manual entry helpers ───────────────────────────────────────────────
  const mif = (key) => (e) => setManualItemForm((p) => ({ ...p, [key]: e.target.value }));
  const mpf = (key) => (e) => setManualPackForm((p) => ({ ...p, [key]: e.target.value }));

  const handleAddManualItem = () => {
    if (!manualItemForm.totalCostEur) {
      toast.error("Total Cost EUR is required.");
      return;
    }
    const newRow = {
      itemCode:           manualItemForm.itemCode           || "",
      itemName:           manualItemForm.itemName           || "",
      quantity:           Number(manualItemForm.quantity)   || 0,
      unit:               manualItemForm.unit               || "Kg",
      unitPriceValue:     Number(manualItemForm.unitPriceValue)   || 0,
      unitPriceCurrency:  manualItemForm.unitPriceCurrency  || "EUR",
      totalPriceValue:    Number(manualItemForm.totalPriceValue)  || 0,
      totalPriceCurrency: manualItemForm.totalPriceCurrency || "EUR",
      totalPriceEur:      Number(manualItemForm.totalPriceEur)    || 0,
      customsDuty:        manualItemForm.customsDuty        || "",
      customsDutyRate:    manualItemForm.customsDutyRate    || "",
      customsDutyEur:     Number(manualItemForm.customsDutyEur)   || 0,
      totalCostEur:       Number(manualItemForm.totalCostEur),
      matched:            false,
      matchedBlendItem:   null,
    };
    if (editingItemIndex !== null) {
      setParsedItemCosts((prev) => prev.map((r, i) => i === editingItemIndex ? newRow : r));
      setEditingItemIndex(null);
    } else {
      setParsedItemCosts((prev) => [...prev, newRow]);
    }
    setManualItemForm({});
    setShowManualItemForm(false);
  };

  const handleAddManualPack = () => {
    if (!manualPackForm.totalCostEur) {
      toast.error("Total Cost EUR is required.");
      return;
    }
    const newRow = {
      itemCode:           manualPackForm.itemCode           || "",
      name:               manualPackForm.name               || "",
      quantity:           Number(manualPackForm.quantity)   || 0,
      unit:               manualPackForm.unit               || "Pcs",
      unitPriceValue:     Number(manualPackForm.unitPriceValue)   || 0,
      unitPriceCurrency:  manualPackForm.unitPriceCurrency  || "EUR",
      totalPriceValue:    Number(manualPackForm.totalPriceValue)  || 0,
      totalPriceCurrency: manualPackForm.totalPriceCurrency || "EUR",
      totalPriceEur:      Number(manualPackForm.totalPriceEur)    || 0,
      totalCostEur:       Number(manualPackForm.totalCostEur),
    };
    if (editingPackIndex !== null) {
      setParsedPackagingCosts((prev) => prev.map((r, i) => i === editingPackIndex ? newRow : r));
      setEditingPackIndex(null);
    } else {
      setParsedPackagingCosts((prev) => [...prev, newRow]);
    }
    setManualPackForm({});
    setShowManualPackForm(false);
  };

  // ── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaveError(null);
    if (!isEdit && (!blendId || !versionId)) {
      toast.error(
        saveAsBlend
          ? "Create the blend version first using the 'Create Version' button."
          : "Please select a blend and version."
      );
      return;
    }
    if (!dietOutNet || Number.isNaN(Number(dietOutNet))) {
      toast.error("Please enter a valid total amount (kg).");
      return;
    }

    try {
      setSaving(true);

      if (isEdit) {
        const payload = {
          batchNumber: batchNumber || null,
          dietOutNet: Number(dietOutNet),
          recordStatus,
          ...(primaryId        ? { primaryId }                              : {}),
          ...(primaryCode      ? { primaryCode }                            : {}),
          ...(primaryGradeName ? { primaryGradeName }                      : {}),
          ...(primaryProcessingDate ? { primaryProcessingDate }            : {}),
          ...(rawIn            ? { rawIn: Number(rawIn) }                  : {}),
          ...(primaryOut       ? { primaryOut: Number(primaryOut) }        : {}),
          ...(dietIn           ? { dietIn: Number(dietIn) }                : {}),
          ...(dietId           ? { dietId }                                : {}),
          ...(dietCode         ? { dietCode }                              : {}),
          ...(dietGradeName    ? { dietGradeName }                         : {}),
          ...(processingFeeTotal > 0 ? { processingFeeEur: processingFeeTotal } : {}),
          ...(adminCostRate && parseFloat(adminCostRate) > 0 ? { adminCostEurPerKg: parseFloat(adminCostRate) } : {}),
          ...(totalCostEurCalc > 0   ? { totalCostEur: totalCostEurCalc }       : {}),
          ...(unitPriceEurCalc        ? { unitPriceEur: unitPriceEurCalc }       : {}),
        };
        const res = await updateBatchApi(
          editBatch.blendId,
          editBatch.blendVersionId,
          editBatch.id,
          payload
        );
        toast.success("Batch updated.");
        onSaved(
          res?.data || { ...editBatch, ...payload },
          editBatch.blendId,
          editBatch.blendVersionId
        );
      } else {
        const fxRateUsdToEur =
          parseFloat(eurHufRate) > 0 && parseFloat(usdHufRate) > 0
            ? parseFloat(usdHufRate) / parseFloat(eurHufRate)
            : null;
        const fxRateHufToEur =
          parseFloat(eurHufRate) > 0 ? 1 / parseFloat(eurHufRate) : null;

        const costComponents = [
          ...parsedItemCosts.map((r) => ({
            componentType:     "TOBACCO",
            itemCode:          r.itemCode || null,
            description:       r.itemName || null,
            quantity:          r.quantity,
            unit:              r.unit || "Kg",
            currency:          r.unitPriceCurrency || null,
            unitCostOriginal:  r.unitPriceValue   || null,
            totalPriceOriginal: r.totalPriceValue  || null,
            totalPriceCurrency: r.totalPriceCurrency || null,
            totalPriceEur:     r.totalPriceEur     || null,
            customsDuty:       r.customsDuty       || null,
            customsDutyRate:   r.customsDutyRate   || null,
            customsDutyEur:    r.customsDutyEur    || null,
            totalCostEur:      r.totalCostEur      || null,
            fxRateToEur:       parseFloat(eurHufRate) > 0 ? parseFloat(eurHufRate) : null,
            fxRateUsdToHuf:    parseFloat(usdHufRate) > 0 ? parseFloat(usdHufRate) : null,
            fxRateDate:        dateToMs(mnbRateDate),
          })),
          ...parsedPackagingCosts.map((r) => ({
            componentType:     "PACKAGING",
            itemCode:          r.itemCode || null,
            description:       r.name     || null,
            quantity:          r.quantity,
            unit:              r.unit     || "Pcs",
            currency:          r.unitPriceCurrency || null,
            unitCostOriginal:  r.unitPriceValue   || null,
            totalPriceOriginal: r.totalPriceValue  || null,
            totalPriceCurrency: r.totalPriceCurrency || null,
            totalPriceEur:     r.totalPriceEur     || null,
            totalCostEur:      r.totalCostEur      || null,
            fxRateToEur:       parseFloat(eurHufRate) > 0 ? parseFloat(eurHufRate) : null,
            fxRateUsdToHuf:    parseFloat(usdHufRate) > 0 ? parseFloat(usdHufRate) : null,
            fxRateDate:        dateToMs(mnbRateDate),
          })),
        ];

        const hasCostData = costComponents.length > 0;

        const payload = {
          batchNumber: batchNumber || null,
          dietOutNet: Number(dietOutNet),
          ...(primaryId        ? { primaryId }                              : {}),
          ...(primaryCode      ? { primaryCode }                            : {}),
          ...(primaryGradeName ? { primaryGradeName }                      : {}),
          ...(primaryProcessingDate ? { primaryProcessingDate }            : {}),
          ...(rawIn            ? { rawIn: Number(rawIn) }                  : {}),
          ...(primaryOut       ? { primaryOut: Number(primaryOut) }        : {}),
          ...(dietIn           ? { dietIn: Number(dietIn) }                : {}),
          ...(dietId           ? { dietId }                                : {}),
          ...(dietCode         ? { dietCode }                              : {}),
          ...(dietGradeName    ? { dietGradeName }                         : {}),
          ...(processingFeeTotal > 0 ? { processingFeeEur: processingFeeTotal } : {}),
          ...(adminCostRate && parseFloat(adminCostRate) > 0 ? { adminCostEurPerKg: parseFloat(adminCostRate) } : {}),
          ...(hasCostData && tobaccoPgEur > 0 ? { tobaccoPgEur } : {}),
          ...(hasCostData && packagingMaterialEur > 0 ? { packagingMaterialEur } : {}),
          ...(totalCostEurCalc > 0   ? { totalCostEur: totalCostEurCalc }       : {}),
          ...(unitPriceEurCalc        ? { unitPriceEur: unitPriceEurCalc }       : {}),
          ...(processingDate ? { dietProcessingDate: dateToMs(processingDate) } : {}),
          ...(mnbRateDate ? { productionFxRateDate: dateToMs(mnbRateDate) } : {}),
          ...(eurHufRate && parseFloat(eurHufRate) > 0
            ? { productionFxRateToEur: parseFloat(eurHufRate) }
            : {}),
          ...(usdHufRate && parseFloat(usdHufRate) > 0
            ? { productionFxRateUsdToHuf: parseFloat(usdHufRate) }
            : {}),
          ...(hasCostData ? { costComponents } : {}),
        };

        const res = await createBatchForBlendVersionApi(blendId, versionId, payload);
        toast.success("Batch created.");
        onSaved(res?.data || payload, blendId, versionId);
      }
      onClose();
    } catch (err) {
      console.error(err);
      const backendMsg = err?.response?.data?.message;
      setSaveError(
        backendMsg || (isEdit ? "Could not update batch." : "Could not create batch.")
      );
    } finally {
      setSaving(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onClose={onClose} maxWidth={isEdit ? "sm" : "xl"} fullWidth>
      <DialogTitle>{isEdit ? "Edit Batch" : "Add New Batch"}</DialogTitle>
      <DialogContent>
        {/* Edit mode info banner */}
        {isEdit && (
          <Box sx={{ mb: 1, p: 1, bgcolor: "#f5f5f5", borderRadius: 1 }}>
            <strong>Blend:</strong> {editBatch?.blendName || editBatch?.blendId || "—"}
            {"  "}
            <strong>Version:</strong>{" "}
            {editBatch?.blendVersionName || editBatch?.blendVersionId || "—"}
          </Box>
        )}

        {/* Create mode: blend / version selection (hidden when saveAsBlend is ON) */}
        {!isEdit && !saveAsBlend && (
          <>
            <FormControl fullWidth margin="normal" disabled={loadingBlends}>
              <InputLabel id="add-batch-blend-label">Blend</InputLabel>
              <Select
                labelId="add-batch-blend-label"
                value={blendId}
                label="Blend"
                onChange={handleBlendChange}
              >
                {blends.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.name || b.code || b.id}
                  </MenuItem>
                ))}
              </Select>
              {loadingBlends && (
                <Box sx={{ display: "flex", mt: 0.5 }}>
                  <CircularProgress size={16} />
                </Box>
              )}
            </FormControl>

            <FormControl
              fullWidth
              margin="normal"
              disabled={!blendId || loadingVersions}
            >
              <InputLabel id="add-batch-version-label">Blend Version</InputLabel>
              <Select
                labelId="add-batch-version-label"
                value={versionId}
                label="Blend Version"
                onChange={handleVersionChange}
              >
                {versions.map((v) => (
                  <MenuItem key={v.id} value={v.id}>
                    {v.versionName || v.id}
                  </MenuItem>
                ))}
              </Select>
              {loadingVersions && (
                <Box sx={{ display: "flex", mt: 0.5 }}>
                  <CircularProgress size={16} />
                </Box>
              )}
            </FormControl>
          </>
        )}

        <TextField
          fullWidth
          margin="normal"
          label="Batch Number (optional)"
          value={batchNumber}
          onChange={(e) => setBatchNumber(e.target.value)}
        />
        <TextField
          fullWidth
          margin="normal"
          label="Total Amount in Blend (kg)"
          type="number"
          value={dietOutNet}
          onWheel={(e) => e.target.blur()}
          disabled
          onChange={(e) => setDietOutNet(e.target.value)}
        />

        {/* ── Primary / DIET process info ─────────────────────────────── */}
        <Accordion sx={{ mt: 2 }} disableGutters TransitionProps={{ unmountOnExit: true }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight="bold">
              Primary &amp; DIET Process Info
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                (auto-filled when pasting Excel data)
              </Typography>
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
              {/* ── Primary column ── */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Primary Process</Typography>
                <TextField fullWidth size="small" margin="dense" label="Production ID"     value={primaryId}             onChange={(e) => setPrimaryId(e.target.value)} />
                <TextField fullWidth size="small" margin="dense" label="Blend ID (Code)"   value={primaryCode}           onChange={(e) => setPrimaryCode(e.target.value)} />
                <TextField fullWidth size="small" margin="dense" label="Blend Name"        value={primaryGradeName}      onChange={(e) => setPrimaryGradeName(e.target.value)} />
                <TextField fullWidth size="small" margin="dense" label="Processing Date"   value={primaryProcessingDate} onChange={(e) => setPrimaryProcessingDate(e.target.value)} placeholder="YYYY-MM-DD" />
                <TextField fullWidth size="small" margin="dense" label="Input (kg)"        value={rawIn}                 onChange={(e) => setRawIn(e.target.value)}       type="number" />
                <TextField fullWidth size="small" margin="dense" label="Output (kg)"       value={primaryOut}            onChange={(e) => setPrimaryOut(e.target.value)}  type="number" />
              </Box>
              {/* ── DIET column ── */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>DIET Process</Typography>
                <TextField fullWidth size="small" margin="dense" label="Production ID"     value={dietId}                onChange={(e) => setDietId(e.target.value)} />
                <TextField fullWidth size="small" margin="dense" label="Blend ID (Code)"   value={dietCode}              onChange={(e) => setDietCode(e.target.value)} />
                <TextField fullWidth size="small" margin="dense" label="Blend Name"        value={dietGradeName}         onChange={(e) => setDietGradeName(e.target.value)} />
                <TextField fullWidth size="small" margin="dense" label="Processing Date"   value={processingDate}        onChange={(e) => setProcessingDate(e.target.value)} placeholder="YYYY-MM-DD" />
                <TextField fullWidth size="small" margin="dense" label="Input (kg)"        value={dietIn}                onChange={(e) => setDietIn(e.target.value)}      type="number" />
                <TextField fullWidth size="small" margin="dense" label="Output (kg)"       value={dietOutNet}            onChange={(e) => setDietOutNet(e.target.value)}  type="number" helperText="Same as 'Total Amount' above" />
                <TextField fullWidth size="small" margin="dense" label="Processing Fee (EUR/kg)" value={processingFeeRate} onChange={(e) => setProcessingFeeRate(e.target.value)} type="number" inputProps={{ step: "0.0001", min: "0" }} helperText={processingFeeTotal > 0 ? `Total: ${processingFeeTotal.toFixed(2)} EUR` : "rate × output kg"} />
                <TextField fullWidth size="small" margin="dense" label="Admin Cost (EUR/kg)" value={adminCostRate} onChange={(e) => { setAdminCostRate(e.target.value); setAdminCostRateOverridden(true); }} type="number" inputProps={{ step: "0.0001", min: "0" }} helperText={`${new Date().getFullYear()} yearly rate — snapshotted per batch`} />
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
        {isEdit && (
          <FormControl fullWidth margin="normal">
            <InputLabel id="edit-batch-status-label">Status</InputLabel>
            <Select
              labelId="edit-batch-status-label"
              value={recordStatus}
              label="Status"
              onChange={(e) => setRecordStatus(e.target.value)}
            >
              {["ACTIVE", "INACTIVE", "CANCELLED"].map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* ═══ Manual cost entry – create mode only ══════════════════════ */}
        {!isEdit && (
          <Accordion sx={{ mt: 2 }} disableGutters TransitionProps={{ unmountOnExit: true }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight="bold" sx={{ flexGrow: 1 }}>
                Manual Cost Entry&nbsp;
                <Typography component="span" variant="caption" color="text.secondary">
                  (add blend item &amp; packaging rows by hand)
                </Typography>
              </Typography>
              {(parsedItemCosts.length > 0 || parsedPackagingCosts.length > 0) && (
                <Chip label={`${parsedItemCosts.length + parsedPackagingCosts.length} row(s)`} size="small" color="success" sx={{ mr: 1 }} />
              )}
            </AccordionSummary>
            <AccordionDetails>

              {/* ─── Blend Items ─── */}
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Blend Items (Tobacco)</Typography>

              {parsedItemCosts.length > 0 && (
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 1.5, maxHeight: 240 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Item Code</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Item Name</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Qty</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Unit</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Unit Price</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Cur.</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Total Price</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Cur.</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>→EUR</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Customs Duty</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Rate</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Cust. EUR</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#f0f4c3" }}>Total Cost EUR</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {parsedItemCosts.map((r, i) => (
                        <TableRow key={i} hover>
                          <TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>{r.itemCode}</TableCell>
                          <TableCell sx={{ fontSize: "0.75rem" }}>{r.itemName}</TableCell>
                          <TableCell align="right">{r.quantity}</TableCell>
                          <TableCell>{r.unit}</TableCell>
                          <TableCell align="right">{r.unitPriceValue || ""}</TableCell>
                          <TableCell>{r.unitPriceCurrency}</TableCell>
                          <TableCell align="right">{r.totalPriceValue || ""}</TableCell>
                          <TableCell>{r.totalPriceCurrency}</TableCell>
                          <TableCell align="right">{r.totalPriceEur || ""}</TableCell>
                          <TableCell sx={{ fontSize: "0.75rem" }}>{r.customsDuty}</TableCell>
                          <TableCell sx={{ fontSize: "0.75rem" }}>{r.customsDutyRate}</TableCell>
                          <TableCell align="right">{r.customsDutyEur || ""}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#f9fbe7" }}>{r.totalCostEur.toFixed(2)}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            <Tooltip title="Edit row">
                              <IconButton size="small" color="primary" onClick={() => { setManualItemForm({ ...r }); setEditingItemIndex(i); setShowManualItemForm(true); }}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remove row">
                              <IconButton size="small" color="error" onClick={() => setParsedItemCosts((p) => p.filter((_, j) => j !== i))}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {showManualItemForm ? (
                <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
                  <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 1 }}>{editingItemIndex !== null ? "Edit Blend Item Row" : "New Blend Item Row"}</Typography>
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1 }}>
                    <TextField size="small" label="Item Code" value={manualItemForm.itemCode || ""} onChange={mif("itemCode")} />
                    <TextField size="small" label="Item Name" value={manualItemForm.itemName || ""} onChange={mif("itemName")} />
                    <TextField size="small" label="Quantity" type="number" value={manualItemForm.quantity || ""} onChange={mif("quantity")} />
                    <TextField size="small" label="Unit" value={manualItemForm.unit || ""} onChange={mif("unit")} placeholder="Kg" />
                    <TextField size="small" label="Unit Price" type="number" value={manualItemForm.unitPriceValue || ""} onChange={mif("unitPriceValue")} />
                    <TextField size="small" label="Unit Price Cur." value={manualItemForm.unitPriceCurrency || ""} onChange={mif("unitPriceCurrency")} placeholder="EUR" />
                    <TextField size="small" label="Total Price" type="number" value={manualItemForm.totalPriceValue || ""} onChange={mif("totalPriceValue")} />
                    <TextField size="small" label="Total Price Cur." value={manualItemForm.totalPriceCurrency || ""} onChange={mif("totalPriceCurrency")} placeholder="EUR" />
                    <TextField size="small" label="Total Price (EUR)" type="number" value={manualItemForm.totalPriceEur || ""} onChange={mif("totalPriceEur")} />
                    <TextField size="small" label="Customs Duty" value={manualItemForm.customsDuty || ""} onChange={mif("customsDuty")} />
                    <TextField size="small" label="Customs Rate" value={manualItemForm.customsDutyRate || ""} onChange={mif("customsDutyRate")} />
                    <TextField size="small" label="Customs EUR" type="number" value={manualItemForm.customsDutyEur || ""} onChange={mif("customsDutyEur")} />
                    <TextField size="small" label="Total Cost EUR *" required type="number" value={manualItemForm.totalCostEur || ""} onChange={mif("totalCostEur")} />
                  </Box>
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Button size="small" variant="contained" onClick={handleAddManualItem}>{editingItemIndex !== null ? "Update Row" : "Add Row"}</Button>
                    <Button size="small" onClick={() => { setShowManualItemForm(false); setManualItemForm({}); setEditingItemIndex(null); }}>Cancel</Button>
                  </Stack>
                </Paper>
              ) : (
                <Button size="small" variant="outlined" onClick={() => setShowManualItemForm(true)} sx={{ mb: 2 }}>+ Add Blend Item Row</Button>
              )}

              <Divider sx={{ my: 1.5 }} />

              {/* ─── Packaging ─── */}
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Packaging / Non-Tobacco</Typography>

              {parsedPackagingCosts.length > 0 && (
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 1.5, maxHeight: 240 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Code</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Qty</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Unit</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Unit Price</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Cur.</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Total Price</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Cur.</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>→EUR</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#bbdefb" }}>Total Cost EUR</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {parsedPackagingCosts.map((r, i) => (
                        <TableRow key={i} hover>
                          <TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>{r.itemCode}</TableCell>
                          <TableCell sx={{ fontSize: "0.75rem" }}>{r.name}</TableCell>
                          <TableCell align="right">{r.quantity}</TableCell>
                          <TableCell>{r.unit}</TableCell>
                          <TableCell align="right">{r.unitPriceValue || ""}</TableCell>
                          <TableCell>{r.unitPriceCurrency}</TableCell>
                          <TableCell align="right">{r.totalPriceValue || ""}</TableCell>
                          <TableCell>{r.totalPriceCurrency}</TableCell>
                          <TableCell align="right">{r.totalPriceEur || ""}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#e3f2fd" }}>{r.totalCostEur.toFixed(2)}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            <Tooltip title="Edit row">
                              <IconButton size="small" color="primary" onClick={() => { setManualPackForm({ ...r }); setEditingPackIndex(i); setShowManualPackForm(true); }}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remove row">
                              <IconButton size="small" color="error" onClick={() => setParsedPackagingCosts((p) => p.filter((_, j) => j !== i))}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {showManualPackForm ? (
                <Paper variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                  <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 1 }}>{editingPackIndex !== null ? "Edit Packaging Row" : "New Packaging Row"}</Typography>
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1 }}>
                    <TextField size="small" label="Code" value={manualPackForm.itemCode || ""} onChange={mpf("itemCode")} />
                    <TextField size="small" label="Name" value={manualPackForm.name || ""} onChange={mpf("name")} />
                    <TextField size="small" label="Quantity" type="number" value={manualPackForm.quantity || ""} onChange={mpf("quantity")} />
                    <TextField size="small" label="Unit" value={manualPackForm.unit || ""} onChange={mpf("unit")} placeholder="Pcs" />
                    <TextField size="small" label="Unit Price" type="number" value={manualPackForm.unitPriceValue || ""} onChange={mpf("unitPriceValue")} />
                    <TextField size="small" label="Unit Price Cur." value={manualPackForm.unitPriceCurrency || ""} onChange={mpf("unitPriceCurrency")} placeholder="EUR" />
                    <TextField size="small" label="Total Price" type="number" value={manualPackForm.totalPriceValue || ""} onChange={mpf("totalPriceValue")} />
                    <TextField size="small" label="Total Price Cur." value={manualPackForm.totalPriceCurrency || ""} onChange={mpf("totalPriceCurrency")} placeholder="EUR" />
                    <TextField size="small" label="Total Price (EUR)" type="number" value={manualPackForm.totalPriceEur || ""} onChange={mpf("totalPriceEur")} />
                    <TextField size="small" label="Total Cost EUR *" required type="number" value={manualPackForm.totalCostEur || ""} onChange={mpf("totalCostEur")} />
                  </Box>
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Button size="small" variant="contained" onClick={handleAddManualPack}>{editingPackIndex !== null ? "Update Row" : "Add Row"}</Button>
                    <Button size="small" onClick={() => { setShowManualPackForm(false); setManualPackForm({}); setEditingPackIndex(null); }}>Cancel</Button>
                  </Stack>
                </Paper>
              ) : (
                <Button size="small" variant="outlined" onClick={() => setShowManualPackForm(true)}>+ Add Packaging Row</Button>
              )}

            </AccordionDetails>
          </Accordion>
        )}

        {/* ═══ Cost info from Excel – create mode only ════════════════════ */}
        {!isEdit && (
          <Accordion sx={{ mt: 2 }} disableGutters TransitionProps={{ unmountOnExit: true }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight="bold" sx={{ flexGrow: 1 }}>
                Cost Info from Excel&nbsp;
                <Typography component="span" variant="caption" color="text.secondary">
                  (optional-paste blend items &amp; packaging costs)
                </Typography>
              </Typography>
              {totalCostEurCalc > 0 && (
                <Chip
                  label={`Total: ${totalCostEurCalc.toFixed(2)} EUR`}
                  size="small"
                  color="primary"
                  sx={{ mr: 1 }}
                />
              )}
            </AccordionSummary>

            <AccordionDetails>
              {/* ── Instruction + column legend ────────────────────────── */}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Select the entire visible range in Excel (e.g. <strong>C1:AE100</strong>),
                copy, and paste below. Blend items, packaging, and exchange rates will be
                extracted automatically from their fixed columns.
              </Typography>
              <Typography variant="caption" fontWeight="bold" display="block" sx={{ mb: 0.5 }}>
                Left table — Blend Items (columns C–P)
              </Typography>
              <ColumnLegend
                columns={[
                  { col: "C", label: "Item Code", color: "#e3f2fd" },
                  { col: "D", label: "Item Name", color: "#f5f5f5" },
                  { col: "E", label: "Qty", color: "#fff3e0" },
                  { col: "F", label: "Unit", color: "#f5f5f5" },
                  { col: "G", label: "Unit Price", color: "#e8f5e9" },
                  { col: "H", label: "Cur.", color: "#e8f5e9" },
                  { col: "I", label: "Total Price", color: "#fce4ec" },
                  { col: "J", label: "Cur.", color: "#fce4ec" },
                  { col: "K", label: "→EUR", color: "#eeeeee" },
                  { col: "L", label: "Customs Duty", color: "#fff8e1" },
                  { col: "M", label: "Rate", color: "#eeeeee" },
                  { col: "N", label: "Customs EUR", color: "#ede7f6" },
                  { col: "O", label: "(skip)", color: "#eeeeee" },
                  { col: "P", label: "Total Cost EUR", color: "#e0f7fa" },
                ]}
              />
              <Typography variant="caption" fontWeight="bold" display="block" sx={{ mt: 1, mb: 0.5 }}>
                Right table — Packaging (U–AD) · Rates block — below right table (U–W)
              </Typography>
              <ColumnLegend
                columns={[
                  { col: "Q–T", label: "(spacer)", color: "#f5f5f5" },
                  { col: "U", label: "Code / Label", color: "#e3f2fd" },
                  { col: "V", label: "Name / Value", color: "#f5f5f5" },
                  { col: "W", label: "Qty / Cur.", color: "#fff3e0" },
                  { col: "X", label: "Unit", color: "#f5f5f5" },
                  { col: "Y", label: "Unit Price", color: "#e8f5e9" },
                  { col: "Z", label: "Cur.", color: "#e8f5e9" },
                  { col: "AA", label: "Total Price", color: "#fce4ec" },
                  { col: "AB", label: "Cur.", color: "#fce4ec" },
                  { col: "AC", label: "→EUR", color: "#eeeeee" },
                  { col: "AD", label: "Total Cost EUR", color: "#e0f7fa" },
                ]}
              />

              {/* ── Single paste area ─────────────────────────────────── */}
              <VirtualizedExcelInput
                tableData={wholeSheetData}
                setTableData={(d) => {
                  setWholeSheetData(d);
                  setParsedItemCosts([]);
                  setParsedPackagingCosts([]);
                }}
                height={320}
              />
              <Box sx={{ mt: 1, mb: 2 }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleParseAll}
                  disabled={!wholeSheetData.length}
                >
                  Parse All
                </Button>
                {!versionId && !saveAsBlend && (
                  <Typography variant="caption" color="warning.main" sx={{ ml: 1 }}>
                    Select a blend version first to enable item matching.
                  </Typography>
                )}
                {!versionId && saveAsBlend && (
                  <Typography variant="caption" color="info.main" sx={{ ml: 1 }}>
                    Version will be created from the section below — item matching not required here.
                  </Typography>
                )}
              </Box>

              {/* ── Exchange rate fields (auto-filled, still editable) ── */}
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                <TextField
                  label="Processing Date"
                  type="date"
                  size="small"
                  value={processingDate}
                  onChange={(e) => setProcessingDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 170 }}
                />
                <TextField
                  label="MNB Rate Date"
                  type="date"
                  size="small"
                  value={mnbRateDate}
                  onChange={(e) => setMnbRateDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 170 }}
                />
                <TextField
                  label="EUR/HUF Rate"
                  type="number"
                  size="small"
                  value={eurHufRate}
                  onChange={(e) => setEurHufRate(e.target.value)}
                  placeholder="e.g. 398.08"
                  inputProps={{ step: "0.01", min: "0" }}
                  sx={{ minWidth: 150 }}
                />
                <TextField
                  label="USD/HUF Rate"
                  type="number"
                  size="small"
                  value={usdHufRate}
                  onChange={(e) => setUsdHufRate(e.target.value)}
                  placeholder="e.g. 344.39"
                  inputProps={{ step: "0.01", min: "0" }}
                  sx={{ minWidth: 150 }}
                />
              </Stack>

              {/* ── Parsed blend items preview ─────────────────────────── */}
              {parsedItemCosts.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="caption" fontWeight="bold">
                      Blend Items: {parsedItemCosts.length} row(s)
                    </Typography>
                    <Chip
                      size="small"
                      color="success"
                      label={`${parsedItemCosts.filter((r) => r.matched).length} matched`}
                    />
                    {parsedItemCosts.some((r) => !r.matched) && (
                      <Chip
                        size="small"
                        color="warning"
                        label={`${parsedItemCosts.filter((r) => !r.matched).length} unmatched`}
                      />
                    )}
                  </Stack>
                  <ParsedItemsTable rows={parsedItemCosts} />
                </>
              )}

              {/* ── Parsed packaging preview ───────────────────────────── */}
              {parsedPackagingCosts.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: "block" }}>
                    Packaging / Non-Tobacco: {parsedPackagingCosts.length} row(s)
                  </Typography>
                  <ParsedPackagingTable rows={parsedPackagingCosts} />
                </>
              )}

              {/* ── Cost Summary ───────────────────────────────────────── */}
              {totalCostEurCalc > 0 && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Cost Summary (will be saved with the batch)
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 1 }}>
                    <Stack spacing={0.25}>
                      <CostSummaryRow
                        label="Tobacco / Blend Total"
                        value={tobaccoPgEur}
                      />
                      <CostSummaryRow
                        label="Packaging Materials Total"
                        value={packagingMaterialEur}
                      />
                      {processingFeeTotal > 0 && (
                        <CostSummaryRow
                          label={`Processing Fee (${Number(processingFeeRate).toFixed(4)} EUR/kg × ${Number(dietOutNet).toLocaleString()} kg)`}
                          value={processingFeeTotal}
                        />
                      )}
                      <Divider />
                      <CostSummaryRow
                        label="Total Cost EUR"
                        value={totalCostEurCalc}
                        highlight
                      />
                      {unitPriceEurCalc && (
                        <CostSummaryRow
                          label={`Unit Price EUR/kg (÷ ${Number(dietOutNet).toFixed(2)} kg)`}
                          value={unitPriceEurCalc}
                          highlight
                        />
                      )}
                    </Stack>
                  </Paper>
                </>
              )}
            </AccordionDetails>
          </Accordion>
        )}

        {/* ═══ Add New Version to Existing Blend – create mode only ════ */}
        {!isEdit && (
          <Accordion sx={{ mt: 2 }} disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight="bold" sx={{ flexGrow: 1 }}>
                Add New Version to Existing Blend
                {saveAsBlend && blendId && versionId && (
                  <Chip label="✓ Version Created" color="success" size="small" sx={{ ml: 1 }} />
                )}
                {saveAsBlend && !versionId && (
                  <Chip label="In Progress" color="warning" size="small" sx={{ ml: 1 }} />
                )}
              </Typography>
              {!saveAsBlend && (
                <Typography variant="caption" color="text.secondary" sx={{ mr: 1, alignSelf: "center" }}>
                  optional (only if you need to create a blend version from this data)
                </Typography>
              )}
            </AccordionSummary>
            <AccordionDetails>
              <FormControlLabel
                control={
                  <Switch
                    checked={saveAsBlend}
                    onChange={(e) => {
                      setSaveAsBlend(e.target.checked);
                      if (!e.target.checked) {
                        // restore blend/version selection when turning off
                        setBlendId("");
                        setVersionId("");
                        setVersionItems([]);
                        setVersions([]);
                        setSaveAsBlendId("");
                      }
                    }}
                    color="secondary"
                  />
                }
                label="I want to create a new Blend Version from this Excel data"
                sx={{ mb: 2 }}
              />

              {saveAsBlend && (
                <>
                  {/* ── Compact paste zone ────────────────────────────── */}
                  <Box
                    tabIndex={0}
                    onPaste={(e) => {
                      e.preventDefault();
                      const text = e.clipboardData.getData("Text");
                      const rows = text.trim().split("\n").map((r) => r.split("\t"));
                      const maxCols = Math.max(...rows.map((r) => r.length));
                      const data = rows.map((row) =>
                        Array.from({ length: maxCols }, (_, i) => row[i] ?? "")
                      );
                      setWholeSheetData(data);
                      setParsedItemCosts([]);
                      setParsedPackagingCosts([]);
                      setBlendItemRows([]);
                    }}
                    sx={{
                      border: "2px dashed",
                      borderColor: wholeSheetData.length > 0 ? "success.main" : "primary.light",
                      borderRadius: 1,
                      p: 1.5,
                      mb: 2,
                      textAlign: "center",
                      cursor: "text",
                      bgcolor: wholeSheetData.length > 0
                        ? "rgba(76,175,80,0.05)"
                        : "rgba(33,150,243,0.03)",
                      "&:focus": { outline: "2px solid", outlineColor: "primary.main" },
                    }}
                  >
                    {wholeSheetData.length > 0 ? (
                      <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                        <CheckCircleIcon color="success" fontSize="small" />
                        <Typography variant="caption" color="success.main" fontWeight="bold">
                          ✓ Step 1 Done — Excel pasted ({wholeSheetData.length} rows). Now fill in the fields below.
                        </Typography>
                        <Button size="small" variant="text" color="warning" sx={{ fontSize: "0.65rem", py: 0, minWidth: 0 }}
                          onClick={() => { setWholeSheetData([]); setParsedItemCosts([]); setParsedPackagingCosts([]); setBlendItemRows([]); }}>
                          Clear
                        </Button>
                      </Stack>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        <strong>Step 1:</strong> Click here and paste your Excel data (Ctrl+V / Cmd+V)
                      </Typography>
                    )}
                  </Box>

                  {/* Existing blend selector */}
                  <FormControl fullWidth size="small" required sx={{ mb: 2 }}>
                    <InputLabel id="sab-blend-label">Select Existing Blend *</InputLabel>
                    <Select
                      labelId="sab-blend-label"
                      value={saveAsBlendId}
                      label="Select Existing Blend *"
                      onChange={(e) => setSaveAsBlendId(e.target.value)}
                    >
                      {blends.map((b) => (
                        <MenuItem key={b.id} value={b.id}>
                          {b.code || b.name || `Blend #${b.id}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mb: 2 }}>
                    <TextField
                      required
                      size="small"
                      label="Version Name"
                      value={newVersionName}
                      onChange={(e) => setNewVersionName(e.target.value)}
                      helperText="Required"
                    />
                    <FormControl size="small" fullWidth>
                      <InputLabel>Version Status</InputLabel>
                      <Select
                        value={newVersionStatus}
                        label="Version Status"
                        onChange={(e) => setNewVersionStatus(e.target.value)}
                      >
                        <MenuItem value="Actual">Actual</MenuItem>
                        <MenuItem value="Sample">Sample</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField size="small" label="Cut Width"    value={newVersionCutWidth}             onChange={(e) => setNewVersionCutWidth(e.target.value)} />
                    <TextField size="small" label="Moisture"     value={newVersionMoisture}             onChange={(e) => setNewVersionMoisture(e.target.value)} />
                    <TextField size="small" label="Temperature"  value={newVersionTemperature}          onChange={(e) => setNewVersionTemperature(e.target.value)} />
                    <TextField size="small" label="Nicotine Tolerance (%)" type="number" inputProps={{ step: 0.01, min: 0 }} value={newVersionNicotineTolerancePct} onChange={(e) => setNewVersionNicotineTolerancePct(e.target.value)} />
                    <TextField size="small" label="Sugar Tolerance (%)"    type="number" inputProps={{ step: 0.01, min: 0 }} value={newVersionSugarTolerancePct}    onChange={(e) => setNewVersionSugarTolerancePct(e.target.value)} />
                  </Box>

                  {/* ── Step 2: Validate ───────────────────────────────── */}
                  <Paper variant="outlined" sx={{ p: 1.5, mb: 2,
                    bgcolor: blendItemRows.some(r => r.validated) ? "rgba(76,175,80,0.05)" : !wholeSheetData.length ? "grey.50" : "background.paper" }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: blendItemRows.length > 0 ? 1 : 0 }}>
                      <Chip label={blendItemRows.some(r => r.validated) ? "✓ Step 2 Done" : "Step 2"} size="small" sx={{ fontWeight: 700 }}
                        color={blendItemRows.some(r => r.validated) ? "success" : "default"} />
                      <Button variant={blendItemRows.length === 0 ? "contained" : "outlined"} size="small" color="info"
                        onClick={handleValidateBlendItems}
                        disabled={!wholeSheetData.length || validatingBlendItems}
                        startIcon={validatingBlendItems ? <CircularProgress size={14} /> : null}>
                        {validatingBlendItems ? "Validating…" : blendItemRows.length > 0 ? "Re-validate" : "Extract Items & Validate SIs"}
                      </Button>
                      {!wholeSheetData.length && (
                        <Typography variant="caption" color="warning.main">← Paste the Excel first (Step 1 above)</Typography>
                      )}
                      {blendItemRows.length > 0 && !validatingBlendItems && (
                        <>
                          <Chip size="small" color="success" label={`${blendItemRows.filter(r => r.validated).length} validated`} />
                          {blendItemRows.some(r => r.error && !r.error.startsWith("Warning")) && (
                            <Chip size="small" color="error" label={`${blendItemRows.filter(r => r.error && !r.error.startsWith("Warning")).length} errors`} />
                          )}
                          {blendItemRows.some(r => r.error?.startsWith("Warning")) && (
                            <Chip size="small" color="warning" label={`${blendItemRows.filter(r => r.error?.startsWith("Warning")).length} warnings`} />
                          )}
                        </>
                      )}
                    </Stack>

                    {blendItemRows.length > 0 && (
                      <>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
                          Check the Shipping Instruction assigned to each item. If it is wrong, use the dropdown to change it.
                        </Typography>
                        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Grade Mark</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Item Name</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>Qty</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Unit</TableCell>
                                <TableCell sx={{ fontWeight: 700, minWidth: 280 }}>Shipping Instruction</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Item</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Note</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {blendItemRows.map((row, idx) => (
                                <BlendItemValidationRow key={idx} row={row} idx={idx} onSIChange={handleBlendItemRowSIChange} />
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </>
                    )}
                  </Paper>

                  {/* ── Step 3: Create Version ─────────────────────────── */}
                  <Paper variant="outlined" sx={{ p: 1.5,
                    bgcolor: blendId && versionId ? "rgba(76,175,80,0.05)" : blendItemRows.some(r => r.validated) ? "background.paper" : "grey.50" }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Chip label={blendId && versionId ? "✓ Step 3 Done" : "Step 3"} size="small" sx={{ fontWeight: 700 }}
                        color={blendId && versionId ? "success" : "default"} />
                      <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        onClick={handleCreateVersionNow}
                        disabled={creatingVersion || !saveAsBlendId || !newVersionName.trim() || !blendItemRows.some(r => r.validated)}
                        startIcon={creatingVersion ? <CircularProgress size={14} color="inherit" /> : null}
                      >
                        {creatingVersion ? "Creating Version…" : blendId && versionId ? "Re-create Version" : "Create Version & Use for Batch"}
                      </Button>
                      {blendId && versionId ? (
                        <Typography variant="caption" color="success.main" fontWeight="bold">
                          ✓ Version created — open "Cost Info from Excel" below and click "Parse All", then click "Create".
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {!blendItemRows.some(r => r.validated) ? "Complete Step 2 first." : "Creates the version, then you can proceed to parse and save the batch."}
                        </Typography>
                      )}
                    </Stack>
                  </Paper>
                </>
              )}
            </AccordionDetails>
          </Accordion>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={18} /> : isEdit ? "Save" : "Create"}
        </Button>
      </DialogActions>

      {saveError && (
        <Box sx={{ px: 3, pb: 2 }}>
          <Alert
            severity="error"
            onClose={() => setSaveError(null)}
            sx={{ whiteSpace: "pre-line" }}
          >
            {saveError.includes("\n") ? (
              <>
                <strong>Batch creation blocked – insufficient quantities:</strong>
                <ul style={{ margin: "6px 0 0 0", paddingLeft: 18 }}>
                  {saveError
                    .replace(/^Batch creation blocked[^\n]*\n?/, "")
                    .split("\n")
                    .filter(Boolean)
                    .map((line, i) => (
                      <li key={i}>{line.replace(/^[-•]\s*/, "")}</li>
                    ))}
                </ul>
              </>
            ) : (
              saveError
            )}
          </Alert>
        </Box>
      )}
    </Dialog>
  );
}

AddEditBatchDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  editBatch: PropTypes.object,
  initialBlendId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  initialVersionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
